import { JSXElementConstructor, useMemo } from 'react';

import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useCoreProvider } from '../../../provider/useCoreProvider';
import { ErrorCard } from './ErrorCard';
import { ErrorContainer } from './ErrorContainer';
import { ErrorPage } from './ErrorPage';
import {
  BaseErrorProps,
  ErrorBoundaryCardProps,
  ErrorBoundaryContainerProps,
  ErrorBoundaryPageProps,
} from './types';

type DynamicErrorBoundaryProps = {
  children: React.ReactNode;
  FallBackComponent: JSXElementConstructor<BaseErrorProps & { resetError: () => void }>;
  /** If the provider's error boundary component should be used instead of the default error boundary component. This is only used when the utilizeDefaultErrorBoundaryByDefault on the CoreProvider component is false. */
  utilizeDefaultErrorBoundary?: boolean;
};
/** Dynamically switches between the provider's error boundary component and the error boundary component from the core provider */
const DynamicErrorBoundary = ({
  children,
  FallBackComponent,
  utilizeDefaultErrorBoundary,
  ...props
}: DynamicErrorBoundaryProps) => {
  const { errorBoundaryConfig } = useCoreProvider();

  const ErrorBoundary = useMemo(() => {
    if (errorBoundaryConfig?.ErrorBoundaryComponent) {
      return errorBoundaryConfig.ErrorBoundaryComponent;
    }
    return null;
  }, [errorBoundaryConfig?.ErrorBoundaryComponent]);

  const shouldUseProviderErrorBoundary = useMemo(() => {
    if (utilizeDefaultErrorBoundary === true) {
      return false;
    }
    return true;
  }, [utilizeDefaultErrorBoundary]);

  if (ErrorBoundary && shouldUseProviderErrorBoundary) {
    return (
      <ErrorBoundary fallback={(args) => <FallBackComponent {...args} {...props} />}>
        {children}
      </ErrorBoundary>
    );
  }

  return (
    <BaseErrorBoundary
      FallbackComponent={(args) => (
        <FallBackComponent {...args} resetError={() => args.resetErrorBoundary()} {...props} />
      )}
    >
      {children}
    </BaseErrorBoundary>
  );
};

const ErrorBoundaryPage = ({ children, ...props }: ErrorBoundaryPageProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorPage} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundaryContainer = ({ children, ...props }: ErrorBoundaryContainerProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorContainer} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundaryCard = ({ children, ...props }: ErrorBoundaryCardProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorCard} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundary = () => {};

ErrorBoundary.Page = ErrorBoundaryPage;
ErrorBoundary.Container = ErrorBoundaryContainer;
ErrorBoundary.Card = ErrorBoundaryCard;

export { ErrorBoundary };
